// Terrain drawn on squares: deadly, slow, wall, removed, linked, and clone.
// Flat fills with an ink pattern so each reads without a legend.

import { SQUARE_PX } from '../constants';
import type { Rules, Terrain } from '../engine/types';
import { svg } from '../lib/svg';
import { isBoardFlipped } from '../state/localSide';
import { boardOrigin } from './boardOrigin';

const squareX = (square: number): number => boardOrigin(square).x;
const squareY = (square: number): number => boardOrigin(square).y;

/** Ink marks inside a 100 by 100 square, by terrain kind. */
const MARKS: Record<Terrain['kind'], string> = {
  deadly: 'M10 90 L25 55 L35 75 L50 35 L62 70 L75 45 L90 90 Z',
  slow: 'M20 30 A8 8 0 1 1 36 30 A8 8 0 1 1 20 30 Z M58 62 A10 10 0 1 1 78 62 A10 10 0 1 1 58 62 Z M25 70 A6 6 0 1 1 37 70 A6 6 0 1 1 25 70 Z M62 24 A6 6 0 1 1 74 24 A6 6 0 1 1 62 24 Z',
  wall: 'M20 90 C30 60 20 50 40 40 C60 30 50 20 70 10 M40 90 C50 70 45 55 60 50 M30 55 L42 48 M52 32 L64 26 M48 72 L58 66',
  removed: '',
  linked:
    'M50 50 m-30 0 a30 30 0 1 1 60 0 a30 30 0 1 1 -60 0 M50 50 m-18 0 a18 18 0 1 1 36 0 a18 18 0 1 1 -36 0 M50 50 m-7 0 a7 7 0 1 1 14 0 a7 7 0 1 1 -14 0',
  clone: 'M20 26 h38 v38 h-38 Z M42 46 h38 v38 h-38 Z',
};

export const createTerrainLayer = (): { layer: SVGGElement; draw: (rules: Rules) => void } => {
  const layer = svg('g', { class: 'board__terrain' });
  let drawn: Rules['terrain'] | null = null;
  let drawnFlipped = isBoardFlipped();

  const draw = (rules: Rules): void => {
    if (rules.terrain === drawn && drawnFlipped === isBoardFlipped()) return;
    drawn = rules.terrain;
    drawnFlipped = isBoardFlipped();
    const nodes: SVGElement[] = [];
    for (const [key, terrain] of Object.entries(rules.terrain)) {
      const square = Number(key);
      const group = svg('g', {
        class: `terrain terrain--${terrain.kind}`,
        transform: `translate(${squareX(square)} ${squareY(square)})`,
      });
      group.appendChild(
        svg('rect', { class: 'terrain__fill', width: SQUARE_PX, height: SQUARE_PX }),
      );
      if (MARKS[terrain.kind]) {
        group.appendChild(svg('path', { class: 'terrain__mark', d: MARKS[terrain.kind] }));
      }
      if (terrain.plies > 0) {
        group.appendChild(
          svg('text', { class: 'terrain__count', x: SQUARE_PX - 10, y: 24 }, [
            String(Math.ceil(terrain.plies / 2)),
          ]),
        );
      }
      nodes.push(group);
    }
    layer.replaceChildren(...nodes);
  };

  return { layer, draw };
};
